import { Form, OnSubmissionStateChange, useMelioForm } from '@melio/penny';
import { Account, EbillsSubscriptionOptions, Vendor } from '@melio/platform-api';
import { useMelioIntl } from '@melio/platform-i18n';

import { EBillsSusbscriptionFormCompany } from './EBillsSusbscriptionFormCompany';
import { EBillsSusbscriptionFormReceivingOptions } from './EBillsSusbscriptionFormReceivingOptions';
import { EBillsSusbscriptionFormTerms } from './EBillsSusbscriptionFormTerms';
import { useEBillsSusbscriptionFormSchema } from './schema';
import { EbillsSubscriptionFormFields } from './types';
import { isAddress } from './utils';

export const EBillsSusbscriptionForm = ({
  vendor,
  account,
  ebillsSubscriptionOptions,
  onSubmit,
  onSubmissionStateChange,
  isSaving,
}: {
  vendor: Vendor;
  account: Account;
  ebillsSubscriptionOptions: EbillsSubscriptionOptions;
  onSubmit: (data: EbillsSubscriptionFormFields) => void;
  onSubmissionStateChange: OnSubmissionStateChange<EbillsSubscriptionFormFields>;
  isSaving: boolean;
}) => {
  const { receivingOptions, restriction, trialPeriodDays, isAddressRequired, fields } = ebillsSubscriptionOptions;
  const { formatMessage } = useMelioIntl();
  const schema = useEBillsSusbscriptionFormSchema({ fields, isAddressRequired });
  const { contactFirstName, contactLastName, address } = account.company;

  const { formProps, registerField, watch, setError } = useMelioForm<EbillsSubscriptionFormFields>({
    onSubmit,
    onSubmissionStateChange,
    schema,
    isSaving,
    defaultValues: {
      accountNumber: vendor.accountNumber,
      address: isAddressRequired && isAddress(address) ? address : null,
      name: isAddressRequired
        ? {
            firstName: contactFirstName ?? undefined,
            lastName: contactLastName ?? undefined,
            middleName: null,
          }
        : null,
      receivingOption: receivingOptions[0]?.name,
      acceptedTerms: false,
      fields: fields.reduce((acc, field) => ({ ...acc, [field.name]: field.value }), {}),
    },
  });

  return (
    <Form {...formProps}>
      <Form.TextField
        {...registerField('accountNumber')}
        labelProps={{ label: formatMessage('activities.ebillsSubscriptionForm.form.fields.accountNumber.label') }}
        isRequired
        isReadOnly
      />
      {fields.map((field, index) => (
        <Form.TextField
          key={field.name}
          {...registerField(`fields.${field.name}`)}
          labelProps={{ label: field.name }}
          isRequired
          data-testid={`ebills-subscription-form-field-${index}`}
        />
      ))}
      {isAddressRequired && !!account && (
        <EBillsSusbscriptionFormCompany company={account.company} registerField={registerField} />
      )}
      <EBillsSusbscriptionFormReceivingOptions
        receivingOptions={receivingOptions}
        trialPeriodDays={trialPeriodDays}
        registerField={registerField}
      />
      <EBillsSusbscriptionFormTerms
        selectedReceivingOption={watch('receivingOption')}
        receivingOptions={receivingOptions}
        restriction={restriction}
        registerField={registerField}
        setError={setError}
      />
    </Form>
  );
};
